<template>
  <div
    ref="container"
    v-click-outside="collapseInput"
    class="tag-input"
    :class="{ 'tag-input--expanded': isExpanded }"
    :style="containerStyle"
    @click="expandInput"
  >
    <div class="tag-input__content">
      <span
        v-for="(tag, index) in displayedTags"
        :key="`${tag}-${index}`"
        class="tag-chip"
      >
        <span class="tag-chip__label">{{ tag }}</span>
        <button
          v-if="isExpanded"
          type="button"
          class="tag-chip__remove"
          @click.stop="removeTag(index)"
        >
          &times;
        </button>
      </span>

      <button
        v-if="hiddenTagCount > 0 && !isExpanded"
        ref="overflowBadge"
        type="button"
        class="tag-chip tag-chip--overflow"
        @click.stop="expandFromOverflow"
      >
        +{{ hiddenTagCount }}
      </button>

      <input
        ref="tagInput"
        v-model.trim="draftValue"
        class="tag-input__field"
        :class="{ 'tag-input__field--collapsed': !isExpanded && normalizedTags.length > 0 }"
        type="text"
        :placeholder="normalizedTags.length ? '' : 'Type and press Enter'"
        @focus="expandInput"
        @keydown.enter.prevent="addTag"
      >
    </div>

    <div ref="measure" class="tag-input__measure" aria-hidden="true">
      <span
        v-for="(tag, index) in normalizedTags"
        :key="`measure-${tag}-${index}`"
        class="tag-chip tag-chip--measure"
      >
        <span class="tag-chip__label">{{ tag }}</span>
      </span>
      <span ref="overflowMeasure" class="tag-chip tag-chip--overflow tag-chip--measure">+99</span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ConfigTagInputElement',
  props: {
    value: {
      type: [Array, String],
      default: () => []
    },
    defaultStyle: {
      type: Object,
      default: () => ({})
    }
  },
  data () {
    return {
      draftValue: '',
      isExpanded: false,
      visibleTagCount: 0,
      resizeObserver: null
    }
  },
  computed: {
    normalizedTags () {
      return Array.isArray(this.value)
        ? this.value
        : (typeof this.value === 'string' && this.value.trim() ? [this.value.trim()] : [])
    },
    displayedTags () {
      if (this.isExpanded) {
        return this.normalizedTags
      }
      return this.normalizedTags.slice(0, this.visibleTagCount)
    },
    hiddenTagCount () {
      if (this.isExpanded) {
        return 0
      }
      return Math.max(this.normalizedTags.length - this.visibleTagCount, 0)
    },
    containerStyle () {
      return {
        borderColor: this.defaultStyle.borderColor || '#dfe5ed',
        backgroundColor: this.defaultStyle.backGroundColor || '#ffffff'
      }
    }
  },
  watch: {
    normalizedTags () {
      this.$nextTick(() => this.calculateVisibleTags())
    }
  },
  methods: {
    expandInput () {
      if (!this.isExpanded) {
        this.isExpanded = true
      }
      this.$nextTick(() => this.focusInput())
    },
    expandFromOverflow () {
      this.expandInput()
    },
    collapseInput () {
      this.isExpanded = false
      this.$nextTick(() => this.calculateVisibleTags())
    },
    focusInput () {
      if (this.$refs.tagInput) {
        this.$refs.tagInput.focus()
      }
    },
    addTag () {
      if (!this.draftValue) {
        this.focusInput()
        return
      }

      this.$emit('input', [...this.normalizedTags, this.draftValue])
      this.draftValue = ''
      this.$nextTick(() => {
        this.isExpanded = true
        this.focusInput()
        this.calculateVisibleTags()
      })
    },
    removeTag (index) {
      const nextTags = this.normalizedTags.filter((tag, tagIndex) => tagIndex !== index)
      this.$emit('input', nextTags)
      this.$nextTick(() => {
        if (this.isExpanded) {
          this.focusInput()
        }
        this.calculateVisibleTags()
      })
    },
    getMeasuredTagWidths () {
      const measureEl = this.$refs.measure
      if (!measureEl) {
        return []
      }
      return Array.from(measureEl.querySelectorAll('.tag-chip--measure'))
        .slice(0, this.normalizedTags.length)
        .map(element => Math.ceil(element.offsetWidth))
    },
    getOverflowBadgeWidth (hiddenCount) {
      const overflowMeasureEl = this.$refs.overflowMeasure
      if (!overflowMeasureEl) {
        return 36
      }
      overflowMeasureEl.textContent = `+${hiddenCount}`
      return Math.ceil(overflowMeasureEl.offsetWidth)
    },
    calculateVisibleTags () {
      if (this.isExpanded) {
        this.visibleTagCount = this.normalizedTags.length
        return
      }

      const containerEl = this.$refs.container
      const inputEl = this.$refs.tagInput
      if (!containerEl || !inputEl) {
        this.visibleTagCount = this.normalizedTags.length
        return
      }

      if (!this.normalizedTags.length) {
        this.visibleTagCount = 0
        return
      }

      const contentStyle = window.getComputedStyle(containerEl)
      const horizontalPadding = parseFloat(contentStyle.paddingLeft || 0) + parseFloat(contentStyle.paddingRight || 0)
      const availableWidth = containerEl.clientWidth - horizontalPadding
      const collapsedInputWidth = 12
      const chipGap = 6
      const tagWidths = this.getMeasuredTagWidths()

      let usedWidth = collapsedInputWidth
      let visibleCount = 0

      for (let index = 0; index < tagWidths.length; index++) {
        const remainingCount = this.normalizedTags.length - (index + 1)
        const overflowWidth = remainingCount > 0 ? this.getOverflowBadgeWidth(remainingCount) + chipGap : 0
        const nextWidth = usedWidth + (visibleCount > 0 ? chipGap : 0) + tagWidths[index]

        if (nextWidth + overflowWidth > availableWidth) {
          break
        }

        usedWidth = nextWidth
        visibleCount += 1
      }

      if (visibleCount === 0) {
        this.visibleTagCount = this.normalizedTags.length ? 1 : 0
        return
      }

      this.visibleTagCount = visibleCount
    },
    setupResizeObserver () {
      if (typeof ResizeObserver === 'undefined' || !this.$refs.container) {
        return
      }

      this.resizeObserver = new ResizeObserver(() => {
        this.calculateVisibleTags()
      })
      this.resizeObserver.observe(this.$refs.container)
    }
  },
  mounted () {
    this.$nextTick(() => {
      this.calculateVisibleTags()
      this.setupResizeObserver()
      window.addEventListener('resize', this.calculateVisibleTags)
    })
  },
  beforeDestroy () {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
      this.resizeObserver = null
    }
    window.removeEventListener('resize', this.calculateVisibleTags)
  },
  directives: {
    clickOutside: {
      bind (el, binding) {
        el.__ClickOutsideHandler__ = (event) => {
          if (!(el === event.target || el.contains(event.target))) {
            binding.value()
          }
        }
        document.addEventListener('click', el.__ClickOutsideHandler__)
      },
      unbind (el) {
        document.removeEventListener('click', el.__ClickOutsideHandler__)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.tag-input {
  position: relative;
  width: 100%;
  min-height: 30px;
  border: 1px solid #dfe5ed;
  border-radius: 4px;
  background: #ffffff;
  padding: 3px 8px;
  cursor: text;
  overflow: hidden;

  &:focus-within {
    border-color: #f59e0b;
    box-shadow: 0 0 0 2px rgba(245, 158, 11, 0.15);
  }

  &:hover {
    border-color: #f8b84e;
    box-shadow: 0 0 0 1px rgba(245, 158, 11, 0.18);
  }
}

.tag-input--expanded {
  overflow: visible;
}

.tag-input__content {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 6px;
  width: 100%;
  min-height: 22px;
  overflow: hidden;
}

.tag-input--expanded .tag-input__content {
  align-items: flex-start;
  flex-wrap: wrap;
  overflow: visible;
}

.tag-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 100%;
  border: 1px solid rgba(245, 158, 11, 0.28);
  border-radius: 6px;
  background: #fff3bf;
  color: #8a5a00;
  padding: 4px 8px;
  font-size: var(--text-xs2);
  line-height: 1;
  white-space: nowrap;
  flex: 0 0 auto;
}

.tag-chip__label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tag-chip__remove {
  display: inline-grid;
  place-items: center;
  width: 14px;
  height: 14px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #b36b00;
  cursor: pointer;
  font-size: 12px;
  line-height: 1;
}

.tag-chip--overflow {
  color: #8a5a00;
  font-weight: 500;
}

.tag-input__field {
  flex: 1 1 120px;
  min-width: 80px;
  border: 0;
  background: transparent;
  color: #101828;
  font-size: var(--text-xs2);
  outline: none;
  padding: 4px 0;
}

.tag-input__field--collapsed {
  flex: 0 0 12px;
  min-width: 12px;
  width: 12px;
  padding: 0;
}

.tag-input__measure {
  position: absolute;
  left: 0;
  top: 0;
  visibility: hidden;
  pointer-events: none;
  white-space: nowrap;
  height: 0;
  overflow: hidden;
}

.tag-chip--measure {
  margin-right: 6px;
}
</style>
